import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, TrashIcon } from '@heroicons/react/24/solid';
import { apiUrl, authFetch, authService } from '../utils/api';

interface Question {
  text: string;
  type: 'MultipleChoice' | 'TrueFalse';
  answers: {
    text: string;
    isCorrect: boolean;
  }[];
}

interface LessonData {
  id: number;
  lessonName: string;
  titel?: string;
  type: string;
  videoUrl?: string;
  attachmentUrl?: string;
  attachmentTitel?: string;
  active: boolean;
  order: number;
  unitId: number;
  questions?: Question[];
}

const LessonView = () => {
  const { courseId, lessonId } = useParams<{ courseId: string; lessonId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lessonData, setLessonData] = useState<LessonData | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [isEditing, setIsEditing] = useState(true);
  const [editedData, setEditedData] = useState<Partial<LessonData>>({});
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [newQuestion, setNewQuestion] = useState<Question>({
    text: '',
    type: 'MultipleChoice',
    answers: [
      { text: '', isCorrect: false },
      { text: '', isCorrect: false },
      { text: '', isCorrect: false },
      { text: '', isCorrect: false }
    ]
  });

  // Handle input changes
  const handleInputChange = (field: keyof LessonData, value: string | number) => {
    setEditedData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle save changes
  const handleSave = async () => {
    if (!lessonData) return;

    try {
      const currentQuestions = questions.length > 0 ? questions : (lessonData.questions || []);
      
      const response = await authFetch(apiUrl(`Lesson/${lessonId}`), {
        method: 'PUT',
        headers: {
          'accept': '*/*',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify({
          lessonName: editedData.lessonName || lessonData.lessonName,
          titel: editedData.lessonName || lessonData.lessonName, // Using lessonName as titel
          unitId: lessonData.unitId,
          active: lessonData.active,
          order: editedData.order || lessonData.order,
          type: lessonData.type,
          videoUrl: editedData.videoUrl || lessonData.videoUrl,
          attachmentTitel: editedData.attachmentTitel || lessonData.attachmentTitel,
          attachmentUrl: editedData.attachmentUrl || lessonData.attachmentUrl,
          questions: currentQuestions
        })
      });

      const result = await response.json();

      if (result.success) {
        setSaveMessage({ type: 'success', text: 'تم حفظ التغييرات بنجاح!' });
        setLessonData(prev => ({
          ...prev!,
          ...editedData,
          questions: currentQuestions
        }));
        setIsEditing(false);
        setEditedData({});

        // Clear success message after 3 seconds
        setTimeout(() => {
          setSaveMessage(null);
        }, 3000);
      } else {
        throw new Error(result.message || 'فشل في حفظ التغييرات');
      }
    } catch (error) {
      console.error('Error saving changes:', error);
      setSaveMessage({ type: 'error', text: 'فشل في حفظ التغييرات. يرجى المحاولة مرة أخرى.' });
    }
  };

  // Function to add a new question
  const handleAddQuestion = () => {
    if (newQuestion.text.trim() === '') {
      setSaveMessage({ type: 'error', text: 'نص السؤال لا يمكن أن يكون فارغاً' });
      return;
    }

    // For TrueFalse questions, ensure there are exactly 2 answers: True and False
    let processedQuestion = { ...newQuestion };
    if (newQuestion.type === 'TrueFalse') {
      processedQuestion.answers = [
        { text: 'True', isCorrect: newQuestion.answers.find(a => a.isCorrect)?.text === 'True' || false },
        { text: 'False', isCorrect: newQuestion.answers.find(a => a.isCorrect)?.text === 'False' || false }
      ];
    } else {
      // For MultipleChoice, filter out empty answers
      processedQuestion.answers = newQuestion.answers.filter(a => a.text.trim() !== '');
      
      // Ensure at least one answer is marked as correct
      if (!processedQuestion.answers.some(a => a.isCorrect)) {
        setSaveMessage({ type: 'error', text: 'يجب تحديد إجابة واحدة على الأقل كصحيحة' });
        return;
      }
    }

    // Add the new question to the questions array
    const updatedQuestions = [...questions, processedQuestion];
    setQuestions(updatedQuestions);
    
    // Reset form
    setNewQuestion({
      text: '',
      type: 'MultipleChoice',
      answers: [
        { text: '', isCorrect: false },
        { text: '', isCorrect: false },
        { text: '', isCorrect: false },
        { text: '', isCorrect: false }
      ]
    });
    
    setShowQuestionForm(false);
    setSaveMessage({ type: 'success', text: 'تم إضافة السؤال بنجاح!' });
    
    // Clear success message after 3 seconds
    setTimeout(() => {
      setSaveMessage(null);
    }, 3000);
  };

  // Handle question input changes
  const handleQuestionChange = (field: keyof Question, value: string | 'MultipleChoice' | 'TrueFalse') => {
    setNewQuestion(prev => ({
      ...prev,
      [field]: value
    }));

    // If type changes to TrueFalse, reset answers
    if (field === 'type' && value === 'TrueFalse') {
      setNewQuestion(prev => ({
        ...prev,
        answers: [
          { text: 'True', isCorrect: false },
          { text: 'False', isCorrect: false }
        ]
      }));
    } else if (field === 'type' && value === 'MultipleChoice') {
      setNewQuestion(prev => ({
        ...prev,
        answers: [
          { text: '', isCorrect: false },
          { text: '', isCorrect: false },
          { text: '', isCorrect: false },
          { text: '', isCorrect: false }
        ]
      }));
    }
  };

  // Handle answer changes
  const handleAnswerChange = (index: number, field: 'text' | 'isCorrect', value: string | boolean) => {
    setNewQuestion(prev => {
      const updatedAnswers = [...prev.answers];
      updatedAnswers[index] = {
        ...updatedAnswers[index],
        [field]: value
      };

      // For TrueFalse questions, ensure only one answer is correct
      if (prev.type === 'TrueFalse' && field === 'isCorrect' && value === true) {
        updatedAnswers.forEach((answer, i) => {
          if (i !== index) {
            updatedAnswers[i] = { ...answer, isCorrect: false };
          }
        });
      }

      return {
        ...prev,
        answers: updatedAnswers
      };
    });
  };

  // Function to delete a question
  const handleDeleteQuestion = (questionIndex: number) => {
    if (window.confirm('هل أنت متأكد من حذف هذا السؤال؟')) {
      const updatedQuestions = questions.filter((_, index) => index !== questionIndex);
      setQuestions(updatedQuestions);
      setSaveMessage({ type: 'success', text: 'تم حذف السؤال. انقر على حفظ لتطبيق التغييرات.' });
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSaveMessage(null);
      }, 3000);
    }
  };

  // Function to convert YouTube URL to embed URL
  const getYouTubeEmbedUrl = (url: string) => {
    if (!url) return '';
    
    // Handle different YouTube URL formats
    let videoId = '';
    
    // Format: https://youtube.com/watch?v=VIDEO_ID
    const watchUrlMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
    if (watchUrlMatch) {
      videoId = watchUrlMatch[1];
    }
    
    // Format: https://youtube.com/embed/VIDEO_ID
    const embedUrlMatch = url.match(/youtube\.com\/embed\/([^&\s]+)/);
    if (embedUrlMatch) {
      videoId = embedUrlMatch[1];
    }
    
    // If we found a video ID, return the embed URL
    return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
  };

  useEffect(() => {
    const fetchLessonData = async () => {
      setLoading(true);
      setError(null);

      try {
        console.log('Fetching lesson data for ID:', lessonId);
        const lessonResponse = await authFetch(apiUrl(`Lesson/${lessonId}`), {
          method: 'GET',
          headers: {
            'accept': '*/*',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        });

        if (!lessonResponse.ok) {
          throw new Error(`HTTP error! status: ${lessonResponse.status}`);
        }

        const responseText = await lessonResponse.text();
        console.log('Raw response:', responseText);

        let lessonResult;
        try {
          lessonResult = JSON.parse(responseText);
        } catch (e) {
          console.error('JSON parse error:', e);
          throw new Error('Invalid JSON response from server');
        }

        if (!lessonResult.success) {
          throw new Error(lessonResult.message || 'فشل في جلب بيانات الدرس');
        }

        console.log('Parsed lesson data:', lessonResult.data);

        // Normalize the data
        const normalizedData = {
          ...lessonResult.data,
          type: (lessonResult.data.type || '').toLowerCase(),
          questions: Array.isArray(lessonResult.data.questions) ? lessonResult.data.questions : []
        };

        console.log('Normalized data:', normalizedData);
        setLessonData(normalizedData);
        
        // Only set questions if they exist and are in the correct format
        if (Array.isArray(normalizedData.questions) && normalizedData.questions.length > 0) {
          setQuestions(normalizedData.questions);
        }

      } catch (error) {
        console.error('Error in fetchLessonData:', error);
        setError(error instanceof Error ? error.message : 'فشل في تحميل بيانات الدرس. يرجى المحاولة مرة أخرى لاحقاً.');
      } finally {
        setLoading(false);
      }
    };

    if (lessonId) {
      fetchLessonData();
    }
  }, [lessonId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex justify-center items-center" dir="rtl">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !lessonData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-8" dir="rtl">
        <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center mb-6">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-blue-600 hover:text-blue-800"
            >
              <ArrowLeftIcon className="h-5 w-5 ml-2" />
              رجوع
            </button>
          </div>
          <div className="text-center text-red-600">
            {error || 'لم يتم العثور على بيانات الدرس'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-8" dir="rtl">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-6">
        {/* Header with back button */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-blue-600 hover:text-blue-800"
          >
            <ArrowLeftIcon className="h-5 w-5 ml-2" />
            رجوع
          </button>
          {isEditing ? (
            <div className="space-x-reverse space-x-2">
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                حفظ
              </button>
              <button
                onClick={() => {
                  setIsEditing(false);
                  setEditedData({});
                }}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
              >
                إلغاء
              </button>
            </div>
          ) : (
            <button
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              تعديل
            </button>
          )}
        </div>

        {/* Save message */}
        {saveMessage && (
          <div
            className={`mb-4 p-3 rounded ${
              saveMessage.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}
          >
            {saveMessage.text}
          </div>
        )}

        {/* Lesson details */}
        <div className="space-y-6">
          <div>
            <h1 className="text-2xl font-bold mb-2">
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedData.lessonName || lessonData.lessonName}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('lessonName', e.target.value)}
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                lessonData.lessonName
                  )}
            </h1>
                </div>

          {/* Video content */}
          {(lessonData.type.toLowerCase() === 'video' || lessonData.type === 'Video') && (
            <div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    رابط الفيديو
                  </label>
                    <input
                    type="text"
                    value={editedData.videoUrl || lessonData.videoUrl || ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('videoUrl', e.target.value)}
                    className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="أدخل رابط فيديو يوتيوب"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Quiz content */}
          {(lessonData.type.toLowerCase() === 'quiz' || lessonData.type === 'Quiz') && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900">أسئلة الاختبار</h2>
                {isEditing && (
                  <button
                    onClick={() => setShowQuestionForm(true)}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center"
                  >
                    <span className="ml-1">+</span> إضافة سؤال
                  </button>
                )}
              </div>
              
              {/* Question Form */}
              {showQuestionForm && isEditing && (
                <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 shadow-sm">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">إضافة سؤال جديد</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        نص السؤال
                      </label>
                      <input
                        type="text"
                        value={newQuestion.text}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleQuestionChange('text', e.target.value)}
                        className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="أدخل سؤالك"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        نوع السؤال
                      </label>
                      <select
                        value={newQuestion.type}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleQuestionChange('type', e.target.value as 'MultipleChoice' | 'TrueFalse')}
                        className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="MultipleChoice">اختيار متعدد</option>
                        <option value="TrueFalse">صح/خطأ</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        الإجابات
                      </label>
                      <div className="space-y-2">
                        {newQuestion.answers.map((answer, index) => (
                          <div key={index} className="flex items-center space-x-reverse space-x-2">
                            <input
                              type="text"
                              value={answer.text}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleAnswerChange(index, 'text', e.target.value)}
                              className="flex-1 px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder={`إجابة ${index + 1}`}
                              disabled={newQuestion.type === 'TrueFalse'}
                            />
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                checked={answer.isCorrect}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleAnswerChange(index, 'isCorrect', e.target.checked)}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <span className="mr-2 text-sm text-gray-600">صحيح</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <div className="flex justify-end space-x-reverse space-x-2 pt-4">
                      <button
                        onClick={() => setShowQuestionForm(false)}
                        className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                      >
                        إلغاء
                      </button>
                      <button
                        onClick={handleAddQuestion}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        إضافة سؤال
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Existing Questions */}
              {questions.map((question, questionIndex) => (
                <div key={questionIndex} className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                  <div className="mb-4 flex justify-between items-start">
                    <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      سؤال {questionIndex + 1}
                    </h3>
                    <p className="mt-2 text-gray-700">{question.text}</p>
                    </div>
                    {isEditing && (
                      <button 
                        onClick={() => handleDeleteQuestion(questionIndex)}
                        className="text-red-500 hover:text-red-700"
                        title="حذف السؤال"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    )}
                </div>

                  <div className="space-y-3">
                    {question.answers.map((answer, answerIndex) => (
                      <div 
                        key={answerIndex}
                        className={`p-3 rounded-lg border ${
                          answer.isCorrect 
                            ? 'bg-green-50 border-green-200' 
                            : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div className="flex items-center">
                          <span className="w-6 h-6 flex items-center justify-center rounded-full border border-gray-300 ml-3">
                            {String.fromCharCode(65 + answerIndex)}
                          </span>
                          <span className={`flex-1 ${answer.isCorrect ? 'font-medium' : ''}`}>
                            {answer.text}
                          </span>
                          {answer.isCorrect && (
                            <span className="mr-3 text-green-600">
                              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              
              {questions.length === 0 && !showQuestionForm && (
                <div className="text-center text-gray-500 py-8">
                  لا توجد أسئلة متاحة لهذا الاختبار.
                  {isEditing && (
                    <div className="mt-2">
                      <button
                        onClick={() => setShowQuestionForm(true)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        أضف السؤال الأول
                      </button>
                    </div>
                  )}
                </div>
              )}
                    </div>
          )}

          {/* Attachment section */}
          {lessonData.attachmentUrl && (
            <div className="mt-4">
              <h3 className="text-lg font-semibold mb-2">المرفق</h3>
              <a
                href={lessonData.attachmentUrl}
                      target="_blank" 
                      rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 flex items-center"
              >
                <span className="ml-2">📎</span>
                {lessonData.attachmentTitel || 'تحميل المرفق'}
              </a>
            </div>
          )}

          {/* Attachment inputs when editing */}
          {isEditing && (
            <div className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  عنوان المرفق
                </label>
                      <input
                        type="text"
                  value={editedData.attachmentTitel || lessonData.attachmentTitel || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('attachmentTitel', e.target.value)}
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="أدخل عنوان المرفق"
                      />
                    </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  رابط المرفق
                </label>
                      <input
                  type="text"
                        value={editedData.attachmentUrl || lessonData.attachmentUrl || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('attachmentUrl', e.target.value)}
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="أدخل رابط المرفق"
                      />
                </div>
              </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default LessonView; 